/* ==========================================================================
   GutRenew TSL — offer block behavior + closing-CTA scroll

   Quantity rows and How Often rows SELECT ONLY. The single [data-tsl-add]
   button is the one and only buy target.

   Subscribe defaults OFF — one-time is pre-selected.

   Prices live in PRICING below, beside the variant they are charged against,
   so the two cannot drift apart.

   CHANGED Aug 2026: the per-day figure has been removed from the quantity
   rows. Rows now show the struck one-time price (when subscribing) and the
   per-bottle price only.

   The offer block is emitted three times on the page. Quantity and purchase
   type are SHARED state — changing either in any block repaints all three,
   so a reader who scrolls past two of them and buys at the third gets what
   they last chose.

   Buying CLEARS THE CART FIRST, then adds, then goes to /checkout. This is a
   single-product lander with no cart page and no nav, so a buyer arriving
   with stale items would otherwise be charged for them without ever seeing
   a cart. If you ever add a second product or an upsell to this page, drop
   the clear step or it will wipe the other line.
   ========================================================================== */

(function () {
  'use strict';

  /* --------------------------------------------------------------- config

     THE ONLY PLACE IDs AND PRICES LIVE. Change them here and nowhere else.

     unit  = per-bottle one-time price
     sub   = per-bottle subscribe price
     Totals shown to the buyer are unit * qty and sub * qty.

     TOTALS ARE THE SOURCE OF TRUTH. NEVER DERIVE THEM BY MULTIPLICATION.

     Verified against live checkout, Aug 18 2026:
                     one-time      subscribe
       1 bottle       $59.99        $53.99
       3 bottles     $164.97       $131.98
       6 bottles     $299.95       $209.97

     An earlier version stored a per-bottle price and computed the total as
     price x quantity. That is not how these variants are priced and it cannot
     reproduce the real numbers:

       - The 6-bottle variant is $299.95, NOT 6 x $49.99 = $299.94. Its true
         unit price is $49.9917, which no two-decimal figure represents.
       - The subscription discount is a percentage applied to the BUNDLE TOTAL
         and then rounded to the cent, so it rounds UP at two tiers:
           3 btl  164.97 x 0.80 = 131.976 -> $131.98  (we showed 131.97)
           6 btl  299.95 x 0.70 = 209.965 -> $209.97  (we showed 209.94)

     Result was a display one to three cents below what checkout charged, on
     the button the buyer is looking at when they commit. Hardcoding the
     verified totals removes the whole class of bug — there is no arithmetic
     left to round wrongly.

     `each` and `subEach` are DISPLAY ONLY, for the "$x each" line on the
     quantity rows. They are truncated, never rounded up, so the unit price is
     never overstated. This means each x quantity can sit a cent or two below
     the total shown in How Often — inherent to bundle pricing, not an error.
     The TOTAL is the number that must match checkout, and it does.

     If you change a price in Shopify, add each tier to cart on the live page,
     read the cart total, and put THAT figure in `total` / `subTotal`.
  ---------------------------------------------------------------------- */
  var PRICING = {
    '1': {
      variant: '47590056394942', plan: '15569387710',
      total: 59.99,  subTotal: 53.99,
      each:  59.99,  subEach:  53.99
    },
    '3': {
      variant: '47590056427710', plan: '15569486014',
      total: 164.97, subTotal: 131.98,
      each:  54.99,  subEach:  43.99
    },
    '6': {
      variant: '47590056460478', plan: '15569518782',
      total: 299.95, subTotal: 209.97,
      each:  49.99,  subEach:  34.99
    }
  };

  var DEFAULT_QTY = '3';
  var SUBSCRIBE_DEFAULT = false;

  /* ------------------------------------------------------ attribution

     WHY THIS EXISTS. Maropost appends tracking parameters to the link, so a
     reader arrives at
       /pages/gut-renew-lander?utm_source=maropost&utm_campaign=...&mp_cid=...
     Those parameters live in the URL of THIS page only. When the buy handler
     redirects to /checkout they are gone, and nothing on the resulting order
     says which email produced it.

     So we copy them onto the line item as properties. Shopify keeps line item
     properties on the order permanently: they show on the order in admin and
     they come out in an order CSV export, which is what lets you join a sale
     back to a specific send.

     Keys are prefixed with "_" — Shopify hides underscore-prefixed properties
     from the customer's view of the cart, checkout and order status page, but
     retains them for the merchant. Do not remove the underscores or buyers
     will see campaign IDs on their receipt.

     NO EMAIL ADDRESSES. Pass a Maropost contact ID, not {{contact.email}}.
     Personal data in a URL ends up in GA4, in referrer headers, and in server
     logs. GA4's terms prohibit it outright, and Shopify already has the
     buyer's email on the order — the contact ID is enough to join the two. */
  var ATTRIB_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'mp_cid',          // Maropost contact id
    'mp_campaign',     // Maropost campaign id / name
    'gclid',
    'fbclid'
  ];

  var ATTRIB_STORE = 'gr_attrib';

  /* How long a click stays credited, in days. Matched to Shopify's own 30-day
     attribution window so the order record and Shopify Analytics agree instead
     of disagreeing on the same sale.

     Stored in localStorage, NOT sessionStorage: a reader who opens the email
     today, thinks it over, and comes back from a bookmark tomorrow should
     still carry the campaign. sessionStorage dies with the tab and lost that
     buyer entirely. */
  var ATTRIB_TTL_DAYS = 30;

  /* ------------------------------------------------------- analytics

     Events are pushed to window.dataLayer for GTM to pick up. If you are on
     raw gtag.js with no container, set this to 'gtag'.

     Do NOT set 'both'. gtag.js writes to window.dataLayer too, so sending
     down both paths double-counts every event. */
  var ANALYTICS_MODE = 'datalayer';   // 'datalayer' | 'gtag' | 'off'

  /* The quantity-row pill is a PERCENTAGE, and which percentage depends on
     what's selected in How Often. Two different discounts, two different
     bases:

       One-time selected -> VOLUME discount, measured against the 1-bottle
                            one-time price ($59.99). This is the "buy more,
                            pay less per bottle" saving.
                              1 btl  59.99 vs 59.99  =  0%  (no pill)
                              3 btl  54.99 vs 59.99  =  8%
                              6 btl  49.99 vs 59.99  = 17%

       Subscribe selected -> SUBSCRIBE discount, measured against THAT tier's
                            own one-time price. This is the ladder the site
                            announcement bar advertises.
                              1 btl  53.99 vs 59.99  = 10%
                              3 btl  43.99 vs 54.99  = 20%
                              6 btl  34.99 vs 49.99  = 30%

     Note the two are not additive and are deliberately not shown together —
     a 6-bottle subscription is 42% below the 1-bottle one-time price, but
     claiming 42% would contradict "Subscribe & Save up to 30%" everywhere
     else on the site. The row shows whichever discount the reader has
     actually selected. */

  /* ---------------------------------------------------------------- state */
  var root = document.getElementById('gr-tsl');
  if (!root) return;

  /* ------------------------------------------------- single-instance guard

     THIS PREVENTS DOUBLE CHARGING. Read before removing.

     If this file is loaded twice — an include left in theme.liquid alongside
     the section's own script tag, the section placed on the page twice, an old
     copy still sitting in a Custom Liquid block — then each copy binds its own
     click handler to the same Add to Cart button. Each copy also has its own
     private `busy` flag, so neither one blocks the other: one tap fires two
     cart/add.js calls and the buyer gets quantity 2 of everything.

     The flag lives on `window`, which is the only thing the two copies share.
     Set AFTER the root lookup above, so a copy that runs before the markup
     exists doesn't lock out the copy that would have worked.

     Fixing the duplicate include is still the right repair — this guard stops
     the symptom, not the cause. */
  if (window.__grTslLoaded) {
    if (window.console) {
      console.warn('[GutRenew TSL] This script is loaded more than once. The ' +
        'duplicate copy has been ignored to prevent double add-to-cart. Find ' +
        'and remove the extra include — check theme.liquid, and check whether ' +
        'the section appears twice on the page.');
    }
    return;
  }
  window.__grTslLoaded = true;

  var offers = Array.prototype.slice.call(root.querySelectorAll('[data-tsl-offer]'));

  var qty = PRICING[DEFAULT_QTY] ? DEFAULT_QTY : '1';
  var subscribe = SUBSCRIBE_DEFAULT;
  var busy = false;

  /* ---------------------------------------------------------------- money */
  function group(n) {
    return String(n).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
  }

  function money(v) {
    var s = Number(v || 0).toFixed(2).split('.');
    return '$' + group(s[0]) + '.' + s[1];
  }

  /* Per-bottle figure for the "$x each" line. Display only — see the note on
     PRICING. Never multiply this by anything. */
  function unitFor(key, wantSub) {
    var p = PRICING[key];
    if (!p) return 0;
    return wantSub ? p.subEach : p.each;
  }

  /* The authoritative number: what checkout will charge. Read straight from
     PRICING, never computed. This is what goes on the button and in the How
     Often rows, and it is the figure the buyer holds us to. */
  function totalFor(key, wantSub) {
    var p = PRICING[key];
    if (!p) return 0;
    return wantSub ? p.subTotal : p.total;
  }

  /* Subscribe saving is measured against THIS tier's own one-time price, so
     it reads 10 / 20 / 30 across the ladder. Do not re-anchor this to the
     1-bottle price — it would produce 10/26/41 and contradict the site's
     "Subscribe & Save up to 30%" copy. */
  /* Computed from the TOTALS, so the badge matches the two figures shown
     beside it rather than a derived unit price. Still lands on 10 / 20 / 30. */
  function subPct(key) {
    var p = PRICING[key];
    if (!p || !p.total) return 0;
    return Math.round((1 - (p.subTotal / p.total)) * 100);
  }

  /* The percentage shown on a quantity row. See the config note above for
     why the base changes with the purchase type. */
  function rowPct(key, wantSub) {
    var p = PRICING[key];
    if (!p) return 0;

    if (wantSub) {
      // Subscribe discount, against this tier's own one-time total.
      if (!p.total) return 0;
      return Math.round((1 - (p.subTotal / p.total)) * 100);
    }

    // Volume discount: this tier's per-bottle price against the 1-bottle price.
    var base = PRICING['1'] ? PRICING['1'].each : 0;
    if (!base) return 0;
    return Math.round((1 - (p.each / base)) * 100);
  }

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ------------------------------------------------- attribution capture */

  /* Read the tracking parameters out of the URL once, on load, and stash them
     for the session. The stash matters because a reader can sit on this page
     for ten minutes, and anything that rewrites the URL — a share sheet, a
     back/forward step, an app's in-page browser — can drop the query string
     before they ever reach the button. */
  function captureAttribution() {
    var found = {};
    var any = false;

    try {
      var q = new URLSearchParams(window.location.search);
      for (var i = 0; i < ATTRIB_PARAMS.length; i++) {
        var k = ATTRIB_PARAMS[i];
        var v = q.get(k);
        if (v) { found[k] = String(v).slice(0, 200); any = true; }
      }
    } catch (e) { /* no URLSearchParams — fall through to the stash */ }

    // Fresh parameters in the URL always win — a newer click overwrites an
    // older stored one, which is the last-touch behaviour Shopify uses too.
    if (any) {
      found._landing = window.location.pathname;
      found._seen = new Date().toISOString();
      found._ts = Date.now();
      try {
        window.localStorage.setItem(ATTRIB_STORE, JSON.stringify(found));
      } catch (e) {
        // Private mode or storage full. The in-memory copy we return still
        // covers this visit, which is the common case.
        try { window.sessionStorage.setItem(ATTRIB_STORE, JSON.stringify(found)); } catch (e2) {}
      }
      return found;
    }

    // No parameters this time — a direct visit, a bookmark, a refresh that
    // dropped the query string. Fall back to the last click we stored, as long
    // as it is still inside the window.
    var stored = null;
    try {
      stored = window.localStorage.getItem(ATTRIB_STORE);
      if (!stored) stored = window.sessionStorage.getItem(ATTRIB_STORE);
    } catch (e) {}

    if (!stored) return null;

    try {
      var prev = JSON.parse(stored);
      var age = Date.now() - (prev._ts || 0);
      if (age > ATTRIB_TTL_DAYS * 86400000) {
        // Expired. Clear it so a months-old campaign can't take credit for a
        // sale it had nothing to do with.
        try { window.localStorage.removeItem(ATTRIB_STORE); } catch (e) {}
        return null;
      }
      return prev;
    } catch (e) {}

    return null;
  }

  var ATTRIB = captureAttribution();

  /* Shape the stash into Shopify line item properties. Human-readable keys,
     because these are read by a person looking at an order in admin. */
  function attributionProperties() {
    if (!ATTRIB) return null;
    var out = {};
    var map = {
      utm_source:  '_Source',
      utm_medium:  '_Medium',
      utm_campaign: '_Campaign',
      utm_content: '_Content',
      utm_term:    '_Term',
      mp_cid:      '_Maropost Contact',
      mp_campaign: '_Maropost Campaign',
      gclid:       '_gclid',
      fbclid:      '_fbclid',
      _landing:    '_Landing Page'
    };
    // _ts and _seen are deliberately not mapped — internal bookkeeping, no
    // reason to put them on an order a human reads.
    var had = false;
    for (var k in map) {
      if (Object.prototype.hasOwnProperty.call(ATTRIB, k) && ATTRIB[k]) {
        out[map[k]] = ATTRIB[k];
        had = true;
      }
    }
    return had ? out : null;
  }

  /* --------------------------------------------------------- analytics */

  function track(name, params) {
    if (ANALYTICS_MODE === 'off') return;

    var payload = { event: name };
    if (params) {
      for (var k in params) {
        if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
      }
    }

    // Campaign context rides along on every event, so you can segment the
    // funnel by send without waiting on GA4's own attribution.
    if (ATTRIB) {
      if (ATTRIB.utm_campaign) payload.gr_campaign = ATTRIB.utm_campaign;
      if (ATTRIB.utm_source) payload.gr_source = ATTRIB.utm_source;
    }

    try {
      if (ANALYTICS_MODE === 'gtag' && typeof window.gtag === 'function') {
        var p = {};
        for (var g in payload) {
          if (g !== 'event' && Object.prototype.hasOwnProperty.call(payload, g)) p[g] = payload[g];
        }
        window.gtag('event', name, p);
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {
      if (window.console) console.warn('[GutRenew TSL] track failed', name, e);
    }
  }

  // Exposed so gutrenew-tsl-tracking.js uses the same push path and the same
  // campaign context, rather than reimplementing both.
  window.grTrack = track;

  /* GA4 ecommerce item for the current selection. */
  function itemPayload() {
    var p = PRICING[qty] || {};
    // value is the real charged total, not unit x quantity. price carries the
    // same total because the cart line IS one bundle at quantity 1 — reporting
    // a per-bottle price against quantity 1 would understate revenue in GA4.
    var tot = totalFor(qty, subscribe);
    return {
      currency: 'USD',
      value: tot,
      items: [{
        item_id: p.variant || '',
        item_name: 'GutRenew',
        item_variant: qty + ' Bottle' + (Number(qty) === 1 ? '' : 's'),
        price: tot,
        quantity: 1
      }],
      gr_qty: Number(qty),
      gr_plan: subscribe ? 'subscription' : 'one_time'
    };
  }

  function planLabel(key) {
    var n = Number(key);
    if (n === 1) return 'Ships now, then every month. Skip, pause or cancel anytime.';
    return 'All ' + n + ' bottles ship now, next in ' + n +
      ' months. Skip, pause or cancel anytime.';
  }

  function afterLabel(key, wantSub) {
    var n = Number(key);
    if (!wantSub) return 'One-time purchase. Nothing recurring.';
    return 'Renews every ' + n + (n === 1 ? ' month' : ' months') + '. Cancel anytime.';
  }

  /* --------------------------------------------------------------- render */
  function render() {
    var onceTotal = totalFor(qty, false);
    var subTotal  = totalFor(qty, true);
    var pct       = subPct(qty);
    var hasPlan   = !!(PRICING[qty] && PRICING[qty].plan);

    offers.forEach(function (offer) {

      /* ---- quantity rows ---- */
      offer.querySelectorAll('[data-tsl-qty]').forEach(function (row) {
        var key = row.getAttribute('data-tsl-qty');
        var p = PRICING[key];
        var on = (key === qty);

        row.classList.toggle('is-on', on);
        row.setAttribute('aria-pressed', on ? 'true' : 'false');
        row.classList.toggle('is-gone', !p);
        if (!p) return;

        var unit = unitFor(key, subscribe);

        var pr = '';
        // Struck price only when subscribing, and only if it's actually higher.
        if (subscribe && p.each > p.subEach) {
          pr += '<span class="tsl-was">' + money(p.each) + '</span>';
        }
        pr += '<span class="tsl-each">' + money(unit) + ' each</span>';

        var prEl = row.querySelector('[data-tsl-pr]');
        if (prEl) prEl.innerHTML = pr;

        // 0% earns no pill — "Save 0%" on the 1-bottle one-time row is worse
        // than nothing there.
        var rp = rowPct(key, subscribe);
        var pillEl = row.querySelector('[data-tsl-pills]');
        if (pillEl) {
          pillEl.innerHTML = rp > 0
            ? '<span class="tsl-pill">Save ' + rp + '%</span>'
            : '';
        }
      });

      /* ---- how often rows ---- */
      offer.querySelectorAll('[data-tsl-pt]').forEach(function (opt) {
        var isSubRow = (opt.getAttribute('data-tsl-pt') === 'sub');
        var on = (isSubRow === subscribe);

        opt.classList.toggle('is-on', on);
        opt.setAttribute('aria-pressed', on ? 'true' : 'false');

        if (isSubRow) {
          opt.disabled = !hasPlan;
          opt.setAttribute('aria-disabled', hasPlan ? 'false' : 'true');
        }
      });

      var onceEl = offer.querySelector('[data-tsl-once]');
      if (onceEl) onceEl.textContent = money(onceTotal);

      var subEl = offer.querySelector('[data-tsl-sub]');
      if (subEl) {
        subEl.innerHTML = hasPlan
          ? money(subTotal) + (subTotal < onceTotal ? '<s>' + money(onceTotal) + '</s>' : '')
          : '';
      }

      var badgeEl = offer.querySelector('[data-tsl-badge]');
      if (badgeEl) {
        var show = hasPlan && pct > 0;
        badgeEl.textContent = show ? 'Save ' + pct + '%' : '';
        badgeEl.style.display = show ? '' : 'none';
      }

      var noteEl = offer.querySelector('[data-tsl-subnote]');
      if (noteEl) {
        noteEl.textContent = hasPlan
          ? planLabel(qty)
          : 'Not available for this supply yet.';
      }

      var afterEl = offer.querySelector('[data-tsl-after]');
      if (afterEl) afterEl.textContent = afterLabel(qty, subscribe);

      /* ---- cta ---- */
      var cta = offer.querySelector('[data-tsl-add]');
      if (cta && !busy) {
        var live = subscribe ? subTotal : onceTotal;
        cta.disabled = !PRICING[qty];
        cta.textContent = PRICING[qty]
          ? 'Add to Cart \u00b7 ' + money(live)
          : 'Unavailable';
      }
    });
  }

  /* --------------------------------------------------------------- wiring */
  offers.forEach(function (offer) {

    offer.querySelectorAll('[data-tsl-qty]').forEach(function (row) {
      row.addEventListener('click', function () {
        if (busy) return;
        var key = row.getAttribute('data-tsl-qty');
        if (!PRICING[key]) return;
        qty = key;
        clearErrors();
        render();
        track('gr_select_quantity', itemPayload());
      });
    });

    offer.querySelectorAll('[data-tsl-pt]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        if (busy || opt.disabled) return;
        subscribe = (opt.getAttribute('data-tsl-pt') === 'sub');
        clearErrors();
        render();
        track('gr_select_plan', itemPayload());
      });
    });

    var cta = offer.querySelector('[data-tsl-add]');
    if (cta) {
      cta.addEventListener('click', function () {
        buy(offer);
      });
    }
  });

  /* ----------------------------------------------------------- add to cart */
  function setBusy(offer, on, label) {
    busy = on;

    // Every CTA on the page locks, not just the clicked one — three blocks,
    // one cart. Only the clicked block changes its label.
    offers.forEach(function (o) {
      var c = o.querySelector('[data-tsl-add]');
      if (!c) return;
      if (o === offer) {
        if (on) {
          if (!c.dataset.label) c.dataset.label = c.textContent;
          c.textContent = label || 'Adding\u2026';
        } else if (c.dataset.label) {
          c.textContent = c.dataset.label;
          delete c.dataset.label;
        }
      }
      c.disabled = on;
      c.classList.toggle('is-busy', on);
    });

    lockRows(on);
  }

  function lockRows(lock) {
    root.querySelectorAll('[data-tsl-qty], [data-tsl-pt]').forEach(function (el) {
      el.classList.toggle('is-locked', lock);
    });
  }

  function showError(offer, msg) {
    var el = offer.querySelector('[data-tsl-err]');
    if (!el) { window.alert(msg); return; }
    el.textContent = msg;
    el.hidden = false;
  }

  function clearErrors() {
    root.querySelectorAll('[data-tsl-err]').forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });
  }

  function postJSON(path, body) {
    return fetch(shopRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (d) { throw d; });
      return res.json();
    });
  }

  function buy(offer) {
    // Local flag stops a double-tap within this instance. The window flag stops
    // a second add from ANY source — another script copy, a theme handler, a
    // synthetic click. Both are checked because they fail in different ways.
    if (busy || window.__grTslBuying) return;
    clearErrors();

    var p = PRICING[qty];
    if (!p || !p.variant) {
      showError(offer, 'That supply option isn\u2019t connected yet. Try another, or contact us.');
      return;
    }

    // Quantity is 1: each tier is its own bundle variant, not a multiple of a
    // single-bottle variant. If that ever changes, change it here.
    var item = { id: Number(p.variant), quantity: 1 };
    if (subscribe && p.plan) item.selling_plan = Number(p.plan);

    // Campaign attribution rides along on the add itself — no extra request,
    // no extra failure mode, and it lands on the order permanently.
    var props = attributionProperties();
    if (props) item.properties = props;

    window.__grTslBuying = true;

    // Fired on CLICK, not on success. This is the number you asked for — how
    // many people pressed the button. Compare it to gr_reach_checkout below to
    // see how many adds actually completed; a gap means cart errors.
    //
    // NAMED gr_add_to_cart_click, NOT add_to_cart, on purpose. Shopify's own
    // Web Pixel (the Google & YouTube app connection) already sends a GA4
    // add_to_cart when the Cart API is hit, which this widget does. Reusing
    // that name would double-count it and corrupt both the GA4 ecommerce
    // reports and any Google Ads conversion built on it. Shopify's event is
    // the authoritative "added to cart"; ours measures button presses, which
    // is a different number — clicks that never completed are exactly the gap
    // we want to see. Do not rename this back.
    track('gr_add_to_cart_click', itemPayload());

    setBusy(offer, true);

    // Clear first so nothing left over from a previous visit rides along to
    // checkout. A failed clear is not fatal — fall through and add anyway
    // rather than block the sale over it.
    postJSON('cart/clear.js', {})
      .catch(function (err) {
        if (window.console) console.warn('[GutRenew TSL] cart clear failed', err);
      })
      .then(function () {
        return postJSON('cart/add.js', { items: [item] });
      })
      .then(function () {
        setBusy(offer, true, 'Redirecting\u2026');
        track('gr_reach_checkout', itemPayload());
        window.location.href = shopRoot() + 'checkout';
      })
      .catch(function (err) {
        // Released only on failure. On success the page is navigating to
        // checkout, so the flag stays set — anything that fires during the
        // redirect must not be able to add a second line.
        window.__grTslBuying = false;
        setBusy(offer, false);
        var msg = (err && (err.description || err.message)) ||
          'We couldn\u2019t start your order. Please try again.';
        showError(offer, msg);
        render();
        track('gr_cart_error', { gr_message: String(msg).slice(0, 120) });
        if (window.console) console.error('[GutRenew TSL]', err);
      });
  }

  /* ------------------------------------------------------- closing CTA jump

     The button at the foot of the page scrolls UP to the offer block above
     it. Because that's a backwards jump, the destination gets a brief ring
     so the reader can see where they were taken — an unannounced upward jump
     is disorienting, especially on a phone where the whole viewport changes
     at once.

     Falls back to the plain href if any of this fails.
  ---------------------------------------------------------------------- */
  root.querySelectorAll('[data-tsl-jump]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      var id = (link.getAttribute('href') || '').replace('#', '');
      var target = id && document.getElementById(id);
      if (!target) return;

      e.preventDefault();

      var reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      try {
        target.scrollIntoView({
          behavior: reduce ? 'auto' : 'smooth',
          block: 'start'
        });
      } catch (err) {
        target.scrollIntoView();
      }

      var box = target.querySelector('[data-tsl-offer]');
      if (!box) return;
      box.classList.add('is-flash');
      window.setTimeout(function () { box.classList.remove('is-flash'); }, 1400);
    });
  });

  /* ------------------------------------------------ back-forward cache reset

     THE PROBLEM. On mobile, swiping back from checkout does not reload this
     page — the browser restores it from the back-forward cache with all
     JavaScript state frozen as it was at navigation. So `busy` is still true,
     window.__grTslBuying is still true, every CTA is still disabled and every
     row still carries .is-locked. The widget comes back dead: the button that
     was used still reads "Redirecting…", the others read "Add to Cart" but
     ignore taps, because setBusy disables all of them and only relabels the
     one that was clicked.

     Nothing resets it on its own. No script re-executes on a bfcache restore,
     which is the entire point of the cache.

     THE FIX. pageshow fires on both a fresh load and a bfcache restore, so it
     is the one hook that covers this. We reset unconditionally rather than only
     when event.persisted is true: on a genuine fresh load `busy` is already
     false and this does nothing, while some browsers restore from history
     without setting persisted at all. Cheap insurance either way.

     The cart is untouched by this — the item they added is still in it. If they
     now buy a different tier, buy() clears the cart first, so they get what
     they last chose rather than both. */
  function unstick() {
    busy = false;
    window.__grTslBuying = false;

    offers.forEach(function (o) {
      var c = o.querySelector('[data-tsl-add]');
      if (!c) return;
      if (c.dataset.label) {
        c.textContent = c.dataset.label;
        delete c.dataset.label;
      }
      c.disabled = false;
      c.classList.remove('is-busy');
    });

    lockRows(false);
    clearErrors();

    // render() rewrites the CTA labels with the live total, so this also
    // repairs "Redirecting…" without needing the stored label.
    if (offers.length) render();
  }

  window.addEventListener('pageshow', unstick);

  /* A restore can also arrive as a visibility change on some mobile browsers
     that keep the page alive in a background tab. Only acts if state is
     actually stuck, so it can't interfere with a genuine in-flight request:
     during a real add, the page is in the foreground. */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && busy) unstick();
  });

  if (offers.length) render();
}());
